import { useState } from 'react';
import AnimatedSection from '../../../components/base/AnimatedSection';

const bundles = [
  {
    id: 'single',
    name: 'Single Beat',
    tag: null,
    beatsIncluded: 1,
    beatsPaid: 1,
    beatsFree: 0,
    pricePerBeat: 150,
    totalPrice: 150,
    originalPrice: 150,
    savings: 0,
    savingsLabel: null,
    features: [
      'Full exclusive rights',
      'Instant download',
      'Commercial use license',
      'WAV file included (MP3 on request via text)',
    ],
    ctaLabel: 'Browse Beats',
    highlight: false,
  },
  {
    id: 'triple',
    name: 'Triple Bundle',
    tag: 'MOST POPULAR',
    beatsIncluded: 4,
    beatsPaid: 3,
    beatsFree: 1,
    pricePerBeat: 250,
    totalPrice: 750,
    originalPrice: 1000,
    savings: 250,
    savingsLabel: 'Save $250',
    features: [
      'Buy 3 beats, get 1 FREE',
      'Full exclusive rights on all 4',
      'Instant download',
      'Commercial use licenses',
      'WAV file included (MP3 on request via text)',
    ],
    ctaLabel: 'Get This Deal',
    highlight: true,
  },
  {
    id: 'ultimate',
    name: 'Ultimate Bundle',
    tag: 'BEST VALUE',
    beatsIncluded: 8,
    beatsPaid: 5,
    beatsFree: 3,
    pricePerBeat: 250,
    totalPrice: 1250,
    originalPrice: 2000,
    savings: 750,
    savingsLabel: 'Save $750',
    features: [
      'Buy 5 beats, get 3 FREE',
      'Full exclusive rights on all 8',
      'Instant download',
      'Commercial use licenses',
      'WAV file included (MP3 on request via text)',
      'Priority email support',
    ],
    ctaLabel: 'Get This Deal',
    highlight: false,
  },
];

export default function BundleDeals() {
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleCTA = (bundle: typeof bundles[0]) => {
    if (bundle.id === 'single') {
      const beatsSection = document.getElementById('beats');
      if (beatsSection) beatsSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      setToastMessage(
        `Add any ${bundle.beatsPaid} beats to your cart and we'll apply your ${bundle.beatsFree} free beat discount at checkout!`
      );
      setToastVisible(true);
      setTimeout(() => setToastVisible(false), 4000);
      const beatsSection = document.getElementById('beats');
      if (beatsSection) {
        setTimeout(() => beatsSection.scrollIntoView({ behavior: 'smooth' }), 300);
      }
    }
  };

  return (
    <section className="py-14 md:py-24 bg-white relative overflow-visible">
      {/* Toast */}
      {toastVisible && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm px-6 py-3 rounded-full shadow-lg max-w-md text-center transition-all duration-300">
          <i className="ri-checkbox-circle-line mr-2 text-green-400"></i>
          {toastMessage}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <AnimatedSection animation="fade-up" className="text-center mb-10 md:mb-16">
          <div className="inline-block px-4 py-2 border border-gray-300 rounded-full text-xs font-semibold text-gray-700 mb-3 md:mb-4 tracking-widest uppercase">
            Bundle Deals
          </div>
          <h2 className="text-3xl md:text-6xl font-bold text-gray-900 mb-3 md:mb-4 leading-tight">
            Stack Beats,<br />Save Big.
          </h2>
          <p className="text-sm md:text-lg text-gray-500 max-w-xl mx-auto">
            The more you grab, the more you save. Build your full catalogue for less.
          </p>
        </AnimatedSection>

        {/* Cards */}
        <div className="grid lg:grid-cols-3 gap-4 md:gap-6 items-stretch">
          {bundles.map((bundle, index) => (
            <AnimatedSection
              key={bundle.id}
              animation="scale-in"
              delay={index * 150}
              className={`relative rounded-2xl p-5 md:p-8 flex flex-col transition-all duration-300 ${
                bundle.highlight
                  ? 'bg-gray-900 text-white ring-2 ring-gray-900 md:scale-105 shadow-xl'
                  : 'bg-gray-50 text-gray-900 border border-gray-200 hover:border-gray-400'
              }`}
            >
              {/* Tag Badge */}
              {bundle.tag && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 bg-red-600 text-white text-xs font-extrabold rounded-full tracking-widest uppercase whitespace-nowrap">
                    {bundle.tag}
                  </span>
                </div>
              )}

              {/* Bundle Name */}
              <div className="mb-6">
                <h3 className={`text-xl font-bold mb-1 ${bundle.highlight ? 'text-white' : 'text-gray-900'}`}>
                  {bundle.name}
                </h3>
                <p className={`text-sm ${bundle.highlight ? 'text-gray-400' : 'text-gray-500'}`}>
                  {bundle.beatsIncluded} beat{bundle.beatsIncluded > 1 ? 's' : ''} total
                  {bundle.beatsFree > 0 && (
                    <span className="ml-1 text-amber-400 font-semibold">
                      ({bundle.beatsFree} free — valued up to ${bundle.pricePerBeat})
                    </span>
                  )}
                </p>
              </div>

              {/* Pricing */}
              <div className="mb-4 md:mb-6">
                <div className="flex items-end gap-3 mb-1">
                  <span className={`text-4xl md:text-5xl font-extrabold tracking-tight ${bundle.highlight ? 'text-white' : 'text-gray-900'}`}>
                    {bundle.id === 'single' ? 'From' : ''} ${bundle.totalPrice.toLocaleString()}
                  </span>
                </div>
                {bundle.originalPrice > bundle.totalPrice ? (
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-sm line-through ${bundle.highlight ? 'text-gray-500' : 'text-gray-400'}`}>
                      ${bundle.originalPrice.toLocaleString()}
                    </span>
                    <span className="text-xs font-bold px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                      {bundle.savingsLabel}
                    </span>
                  </div>
                ) : (
                  <p className={`text-sm mt-1 ${bundle.highlight ? 'text-gray-400' : 'text-gray-500'}`}>
                    Standard rate
                  </p>
                )}
              </div>

              {/* Divider */}
              <div className={`border-t mb-6 ${bundle.highlight ? 'border-white/10' : 'border-gray-200'}`}></div>

              {/* Features */}
              <ul className="space-y-3 flex-1 mb-8">
                {bundle.features.map((feat, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`ri-check-line text-base ${bundle.highlight ? 'text-red-400' : 'text-gray-900'}`}></i>
                    </div>
                    <span className={`text-sm leading-relaxed ${bundle.highlight ? 'text-gray-300' : 'text-gray-600'}`}>
                      {feat}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handleCTA(bundle)}
                className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap ${
                  bundle.highlight
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-gray-900 text-white hover:bg-gray-700'
                }`}
              >
                {bundle.ctaLabel}
              </button>
            </AnimatedSection>
          ))}
        </div>

        {/* Bottom Note */}
        <AnimatedSection animation="fade-in" delay={500}>
          <p className="text-center text-xs text-gray-400 mt-10">
            Bundle discounts are applied at checkout. Each beat comes with a full exclusive license. No expiration on your purchase.
            {' '}WAV included with every purchase —{' '}
            <a href="sms:+15716995343" className="underline hover:text-gray-200 transition-colors whitespace-nowrap">text +1 (571)-699-5343</a>
            {' '}to request your MP3.
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
}
